/**
 * Cross-app authentication synchronization service
 * Handles auth state changes across different app domains/ports using localStorage events
 */

import { getFirebaseAuth } from '../firebase/config';
import { createLogger } from '../utils/logger';

const logger = createLogger('CrossAppAuth');

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Key for storing auth state in localStorage
const AUTH_SYNC_KEY = 'bite_auth_sync';
// Key for preventing auth redirects during logout
const LOGOUT_IN_PROGRESS_KEY = 'bite_logout_in_progress';

// Event type for auth state changes
export interface AuthSyncEvent {
  type: 'signout' | 'signin';
  timestamp: number;
  origin?: string;
}

/**
 * Broadcasts an auth state change to other apps
 */
export const broadcastAuthChange = (type: 'signout' | 'signin'): void => {
  if (!isBrowser) return;

  const event: AuthSyncEvent = {
    type,
    timestamp: Date.now(),
    origin: window.location.origin
  };

  logger.debug('Broadcasting auth change', { type, event });
  window.localStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(event));

  // For signout, set a flag to prevent immediate redirects
  if (type === 'signout') {
    window.localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, Date.now().toString());
    // Clear the flag after a short delay
    setTimeout(() => {
      window.localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
    }, 2000);
  }

  // Remove the item after a short delay to avoid stale events
  setTimeout(() => {
    try {
      const current = window.localStorage.getItem(AUTH_SYNC_KEY);
      if (current && JSON.parse(current).timestamp === event.timestamp) {
        window.localStorage.removeItem(AUTH_SYNC_KEY);
      }
    } catch {
      // Ignore errors
    }
  }, 1000);
};

/**
 * Sets up a listener for cross-app auth state changes
 * Returns a cleanup function
 */
export const setupCrossAppAuthListener = (): (() => void) => {
  if (!isBrowser) {
    return () => {}; // Return no-op cleanup function
  }

  const handleStorageChange = async (event: StorageEvent) => {
    // Only handle our auth sync events
    if (event.key !== AUTH_SYNC_KEY || !event.newValue) {
      return;
    }

    try {
      const authEvent: AuthSyncEvent = JSON.parse(event.newValue);

      // Ignore events from the same origin (our own broadcasts)
      if (authEvent.origin === window.location.origin) {
        return;
      }

      // Ignore old events (older than 5 seconds)
      if (Date.now() - authEvent.timestamp > 5000) {
        return;
      }

      logger.debug('Received auth event from another app', { type: authEvent.type, origin: authEvent.origin });

      const auth = getFirebaseAuth();

      if (authEvent.type === 'signout') {
        // If we have a current user and received a signout event, sign out locally
        if (auth.currentUser) {
          logger.debug('Signing out current user due to cross-app signout');

          // Set logout flag to prevent immediate redirects
          window.localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, Date.now().toString());

          // Sign out silently
          await auth.signOut();

          // Trigger a custom event to let the auth subscription know to clear cache
          try {
            window.dispatchEvent(new CustomEvent('cross-app-signout-complete'));
          } catch {
            // Ignore if window not available
          }

          // Clear the flag after signout is complete
          setTimeout(() => {
            window.localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
          }, 1500);
        }
      }
      // For signin events, we don't need to do anything as Firebase auth state
      // will be updated naturally when the user interacts with the app

    } catch (error) {
      logger.warn('Failed to parse auth sync event', { error: error instanceof Error ? error.message : String(error) });
    }
  };

  // Listen for localStorage changes from other tabs/apps
  window.addEventListener('storage', handleStorageChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange);
  };
};

/**
 * Checks if a logout is currently in progress
 */
export const isLogoutInProgress = (): boolean => {
  if (!isBrowser) return false;

  try {
    const timestamp = window.localStorage.getItem(LOGOUT_IN_PROGRESS_KEY);
    if (!timestamp) return false;

    // Check if the logout flag is recent (within 2 seconds)
    const logoutTime = parseInt(timestamp, 10);
    const now = Date.now();
    const isRecent = (now - logoutTime) < 2000;

    if (!isRecent) {
      // Clean up stale flag
      window.localStorage.removeItem(LOGOUT_IN_PROGRESS_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Enhanced sign out that broadcasts to other apps
 */
export const crossAppSignOut = async (): Promise<void> => {
  const auth = getFirebaseAuth();

  // Broadcast before signing out to ensure other apps get the event
  broadcastAuthChange('signout');

  // Sign out locally
  await auth.signOut();

  logger.debug('Signout completed and broadcasted to other apps');
};
